/* Native community-signup form on /pages/communities.
 *
 * Posts the SAME multipart contract the self-serve SPA uses (a `payload` JSON
 * part, no image) to the worker at <worker_base>/api/submit, which creates the
 * discount + community metaobject and — because we send `marketing_consent`
 * (the form shows the consent line) — a Klaviyo profile on the "Communities
 * Inquiries" list. The worker re-validates everything and is authoritative; the
 * client checks below are UX only.
 *
 * The theme has no build step, so the tier/market/validation rules from the
 * worker's shared/community.ts are mirrored here in plain JS. Keep them in sync.
 */
(function () {
  var form = document.querySelector("[data-community-signup]");
  if (!form) return;

  var WORKER_BASE = (form.getAttribute("data-worker-base") || "https://newcommunity.tincan.kids").replace(/\/+$/, "");
  var TURNSTILE_SITEKEY = form.getAttribute("data-turnstile-sitekey") || "";
  // The active store market (Liquid set it from cart.currency). The market
  // picker is pre-selected to this server-side, and the tier ladder under the
  // devices question is server-rendered for it — so this is the default.
  var ACTIVE_MARKET = form.getAttribute("data-active-market") || "US";

  // The 10–24 starter tier needs a worker that knows it, and the page ships
  // ahead of the worker. So it starts OFF (the old 25-device minimum, matching
  // the server-rendered form) and only turns ON when /api/markets answers with
  // a starter percent for every market (see applyStarterReadiness). A slow,
  // failed or empty response leaves it off.
  var MIN_DEVICES = 25;
  var starterReady = false;
  var MAX_WINDOW_DAYS = 60;
  var DEFAULT_WINDOW_DAYS = 30;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var ORG_TYPES = ["Public School", "Private School", "PTA/Parent Org", "Non-Profit", "Other"];
  var MARKET_HANDLES = ["US", "CA", "AU", "NZ", "GB"];

  // Each market's states/provinces/regions (ENG-2171): [code, name]. The form
  // sends the code; the worker checks it belongs to the market. Mirrors
  // REGIONS in the worker's shared/community.ts and communities-crm's
  // regions.py -- keep the three in step.
  var REGIONS = {
    US: [["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
      ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
      ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
      ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
      ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
      ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"],
      ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
      ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
      ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
      ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
      ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"],
      ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
      ["WI", "Wisconsin"], ["WY", "Wyoming"]],
    CA: [["AB", "Alberta"], ["BC", "British Columbia"], ["MB", "Manitoba"], ["NB", "New Brunswick"],
      ["NL", "Newfoundland and Labrador"], ["NS", "Nova Scotia"], ["NT", "Northwest Territories"],
      ["NU", "Nunavut"], ["ON", "Ontario"], ["PE", "Prince Edward Island"], ["QC", "Quebec"],
      ["SK", "Saskatchewan"], ["YT", "Yukon"]],
    AU: [["ACT", "Australian Capital Territory"], ["NSW", "New South Wales"],
      ["NT", "Northern Territory"], ["QLD", "Queensland"], ["SA", "South Australia"],
      ["TAS", "Tasmania"], ["VIC", "Victoria"], ["WA", "Western Australia"]],
    NZ: [["AUK", "Auckland"], ["BOP", "Bay of Plenty"], ["CAN", "Canterbury"], ["GIS", "Gisborne"],
      ["HKB", "Hawke's Bay"], ["MWT", "Manawatū-Whanganui"], ["MBH", "Marlborough"],
      ["NSN", "Nelson"], ["NTL", "Northland"], ["OTA", "Otago"], ["STL", "Southland"],
      ["TKI", "Taranaki"], ["TAS", "Tasman"], ["WKO", "Waikato"], ["WGN", "Wellington"],
      ["WTC", "West Coast"]],
    GB: [["ENG", "England"], ["SCT", "Scotland"], ["WLS", "Wales"], ["NIR", "Northern Ireland"]]
  };
  var REGION_LABELS = { US: "State", CA: "Province or territory", AU: "State or territory", NZ: "Region", GB: "Nation" };

  // "How did you hear about Tin Can Communities?" codes (ENG-2710), the same
  // as the <select> in connective-form.liquid and the worker's DISCOVERY_SOURCES.
  var DISCOVERY_SOURCES = ["another_group", "parent_group", "tin_can_website", "tin_can_email",
    "tin_can_support", "search", "news_podcast", "smartphone_free_group", "social_post", "event", "other"];

  // Offline fallback (mirrors the worker's MARKET_FALLBACKS — display only).
  var FALLBACK_MARKETS = [
    mkMarket("US", "United States", "USD"),
    mkMarket("CA", "Canada", "CAD"),
    mkMarket("AU", "Australia", "AUD"),
    mkMarket("NZ", "New Zealand", "NZD"),
    mkMarket("GB", "United Kingdom", "GBP")
  ];
  function mkMarket(handle, label, currency) {
    return {
      market_handle: handle,
      label: label,
      currency_code: currency,
      percents: { tier_starter: 15, tier_0: 20, tier_1: 25, tier_2: 30, tier_3: 35 }
    };
  }

  var markets = FALLBACK_MARKETS.slice();
  var marketTouched = false;

  // ── Elements ────────────────────────────────────────────────────────────
  var els = {
    display_name: form.querySelector('[name="display_name"]'),
    organizer_name: form.querySelector('[name="organizer_name"]'),
    organizer_email: form.querySelector('[name="organizer_email"]'),
    organizer_phone: form.querySelector('[name="organizer_phone"]'),
    org_type: form.querySelector('[name="org_type"]'),
    market_handle: form.querySelector("[data-market-select]"),
    city: form.querySelector('[name="city"]'),
    region: form.querySelector("[data-region-select]"),
    discovery_source: form.querySelector("[data-discovery-select]"),
    discovery_source_other: form.querySelector('[name="discovery_source_other"]'),
    target_devices: form.querySelector('[name="target_devices"]'),
    window_opens_at: form.querySelector('[name="window_opens_at"]'),
    window_closes_at: form.querySelector('[name="window_closes_at"]'),
    community_blurb: form.querySelector('[name="community_blurb"]'),
    nonprofit_opt_in: form.querySelector("[data-nonprofit-optin]"),
    nonprofit_name: form.querySelector('[name="nonprofit_name"]')
  };
  var nonprofitOptInField = form.querySelector("[data-nonprofit-optin-field]");
  var nonprofitNameField = form.querySelector("[data-nonprofit-name-field]");
  var regionLabel = form.querySelector("[data-region-label]");
  var discoveryOtherField = form.querySelector("[data-discovery-other-field]");
  var marketHint = form.querySelector("[data-market-hint]");
  var devicesHint = form.querySelector("[data-devices-hint]");
  var submitBtn = form.querySelector("[data-submit]");
  var formError = form.querySelector("[data-form-error]");
  var successBox = document.querySelector("[data-success]");
  var successEmail = document.querySelector("[data-success-email]");

  // ── Date helpers (user's local calendar day) ─────────────────────────────
  function localToday() {
    var d = new Date();
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
  }
  function pad(n) { return String(n).padStart(2, "0"); }
  function addDays(dateStr, n) {
    var d = new Date(dateStr + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }

  var today = localToday();
  els.window_opens_at.value = today;
  els.window_opens_at.min = today;
  els.window_closes_at.value = addDays(today, DEFAULT_WINDOW_DAYS);
  syncCloseBounds();

  els.window_opens_at.addEventListener("change", function () {
    var v = els.window_opens_at.value;
    if (v) {
      var minClose = addDays(v, 1);
      var maxClose = addDays(v, MAX_WINDOW_DAYS);
      if (!els.window_closes_at.value || els.window_closes_at.value < minClose || els.window_closes_at.value > maxClose) {
        els.window_closes_at.value = addDays(v, DEFAULT_WINDOW_DAYS);
      }
    }
    syncCloseBounds();
  });

  function syncCloseBounds() {
    var v = els.window_opens_at.value;
    els.window_closes_at.min = v ? addDays(v, 1) : today;
    els.window_closes_at.max = v ? addDays(v, MAX_WINDOW_DAYS) : "";
  }

  // ── Markets ───────────────────────────────────────────────────────────────
  function currentMarket() {
    var h = els.market_handle.value;
    for (var i = 0; i < markets.length; i++) if (markets[i].market_handle === h) return markets[i];
    return markets[0];
  }

  function tierForDevices(n, percents) {
    if (!Number.isInteger(n) || n < MIN_DEVICES) return null;
    if (n <= 24 && starterReady) return { tier: "tier_starter", percent: percents.tier_starter };
    if (n <= 49) return { tier: "tier_0", percent: percents.tier_0 };
    if (n <= 99) return { tier: "tier_1", percent: percents.tier_1 };
    if (n <= 299) return { tier: "tier_2", percent: percents.tier_2 };
    return { tier: "tier_3", percent: percents.tier_3 };
  }

  // The tier ladder is rendered SERVER-SIDE for the active market (so it's
  // correct on load with no JS / failed fetch). We only repaint it if the user
  // actively changes the market picker.
  function renderMarketHint() {
    if (!marketHint) return;
    var m = currentMarket();
    marketHint.textContent =
      "Discount tiers in " + m.label + ": " +
      (starterReady ? m.percents.tier_starter + "% off for 10–24 devices, " : "") +
      m.percents.tier_0 + "% off for 25–49 devices, " +
      m.percents.tier_1 + "% for 50–99, " +
      m.percents.tier_2 + "% for 100–299, " +
      m.percents.tier_3 + "% for 300+.";
  }

  // The worker answers /api/markets with a tier_starter percent only once it
  // supports the starter tier. Until then keep the 25-device minimum, so the
  // form never offers 10–24 devices (the old worker would reject them) or shows
  // "undefined% off". Turns on by itself when the worker ships.
  function applyStarterReadiness(list) {
    starterReady = list.every(function (m) {
      return m.percents && typeof m.percents.tier_starter === "number";
    });
    MIN_DEVICES = starterReady ? 10 : 25;
    els.target_devices.setAttribute("min", String(MIN_DEVICES));
    if (starterReady) renderMarketHint();
  }

  function renderDevicesHint() {
    if (!devicesHint) return;
    var m = currentMarket();
    var n = parseDevices(els.target_devices.value);
    var tier = tierForDevices(n, m.percents);
    devicesHint.textContent = tier
      ? tier.percent + "% off each device"
      : "Minimum " + MIN_DEVICES + " devices.";
  }

  function rebuildMarketOptions() {
    var prev = els.market_handle.value || ACTIVE_MARKET;
    els.market_handle.innerHTML = "";
    markets.forEach(function (m) {
      var opt = document.createElement("option");
      opt.value = m.market_handle;
      opt.textContent = m.label;
      els.market_handle.appendChild(opt);
    });
    // Default to the active store market; respect a user's explicit choice.
    var pick = marketTouched ? prev : ACTIVE_MARKET;
    if (!hasMarket(pick)) pick = hasMarket(prev) ? prev : (markets[0] ? markets[0].market_handle : "US");
    els.market_handle.value = pick;
  }
  function hasMarket(h) {
    return markets.some(function (m) { return m.market_handle === h; });
  }

  els.market_handle.addEventListener("change", function () {
    marketTouched = true;
    renderMarketHint();
    renderDevicesHint();
    renderNonprofit();
    renderRegions();
  });

  // ── State / province / region (ENG-2171) ──────────────────────────────────
  function regionsFor(h) { return REGIONS[h] || REGIONS.US; }
  function regionName(h, code) {
    var list = regionsFor(h);
    for (var i = 0; i < list.length; i++) if (list[i][0] === code) return list[i][1];
    return "";
  }
  // Rebuilds the list for the picked market. A pick survives only a rebuild
  // for the same market: codes repeat across markets (WA is Washington and
  // Western Australia), so a market switch always clears it.
  var regionMarket = null;
  function renderRegions() {
    var h = els.market_handle.value;
    var prev = h === regionMarket ? els.region.value : "";
    regionMarket = h;
    if (regionLabel) regionLabel.textContent = REGION_LABELS[h] || "State";
    els.region.innerHTML = '<option value="">— Select —</option>';
    regionsFor(h).forEach(function (r) {
      var opt = document.createElement("option");
      opt.value = r[0];
      opt.textContent = r[1];
      els.region.appendChild(opt);
    });
    els.region.value = regionName(h, prev) ? prev : "";
  }
  renderRegions();

  // ── How they heard: Other needs its text (ENG-2710) ───────────────────────
  function renderDiscoveryOther() {
    if (!discoveryOtherField) return;
    discoveryOtherField.hidden = els.discovery_source.value !== "other";
    if (discoveryOtherField.hidden) clearError("discovery_source_other");
  }
  els.discovery_source.addEventListener("change", renderDiscoveryOther);
  renderDiscoveryOther();

  // ── Nonprofit fundraising opt-in (ENG-2035) — US only ─────────────────────
  var NONPROFIT_MARKETS = ["US"];
  function nonprofitOffered() {
    return NONPROFIT_MARKETS.indexOf(els.market_handle.value) !== -1;
  }
  function nonprofitOptedIn() {
    return !!(els.nonprofit_opt_in && els.nonprofit_opt_in.checked && nonprofitOffered());
  }
  function renderNonprofit() {
    if (!nonprofitOptInField) return;
    nonprofitOptInField.hidden = !nonprofitOffered();
    if (nonprofitNameField) nonprofitNameField.hidden = !nonprofitOptedIn();
    if (!nonprofitOptedIn()) clearError("nonprofit_name");
  }
  if (els.nonprofit_opt_in) els.nonprofit_opt_in.addEventListener("change", renderNonprofit);
  renderNonprofit();
  els.target_devices.addEventListener("input", renderDevicesHint);
  // Market ladder is server-rendered for the active market; only the devices
  // hint needs an initial paint. (renderMarketHint runs if the user switches.)
  renderDevicesHint();

  fetch(WORKER_BASE + "/api/markets", { headers: { Accept: "application/json" } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      if (!data || !Array.isArray(data.markets) || data.markets.length === 0) return;
      markets = data.markets;
      applyStarterReadiness(markets);
      rebuildMarketOptions();
      renderNonprofit();
      renderRegions();
      // Refresh the devices hint with live amounts; leave the server-rendered
      // ladder untouched unless the user changes the market.
      renderDevicesHint();
    })
    .catch(function () { /* keep fallback markets — the form must always work */ });

  // ── Turnstile ───────────────────────────────────────────────────────────
  // Some customers' network or security settings (Wi-Fi filters, parental-
  // control routers, ad blockers) block challenges.cloudflare.com, so the
  // checkbox never appears and "complete the verification" is a dead end.
  // After TURNSTILE_TIMEOUT_MS without api.js, or when the widget can't work,
  // a submit shows an email fallback instead, and the worker logs it once per
  // page view. If api.js turns up later, its load event brings the checkbox
  // back.
  var TURNSTILE_TIMEOUT_MS = parseInt(form.getAttribute("data-turnstile-timeout-ms"), 10) || 10000;
  // Challenge failures (300xxx, 600xxx) and timeouts (1106xx) retry inside the
  // widget, so only this many in a row counts as unavailable.
  var TURNSTILE_MAX_RETRYABLE_ERRORS = 3;
  var FALLBACK_EMAIL = "communities@tincan.kids";
  var turnstileToken = null;
  var turnstileWidgetId = null;
  var turnstileUnavailable = false;
  var turnstileErrors = 0;
  var turnstileReported = false;
  var turnstileWaitStart = Date.now();
  var turnstileEl = form.querySelector("[data-turnstile]");

  function reportTurnstileUnavailable(reason, code) {
    if (turnstileReported) return;
    turnstileReported = true;
    try {
      var body = { event: "turnstile_unavailable", reason: reason, form: "theme" };
      if (code) body.error_code = String(code);
      if (navigator.sendBeacon) navigator.sendBeacon(WORKER_BASE + "/api/client-event", JSON.stringify(body));
    } catch (e) { /* logging must never break the form */ }
  }
  function markTurnstileUnavailable(reason, code) {
    turnstileUnavailable = true;
    turnstileToken = null;
    // Only collapse the empty slot; a widget that errored still shows its own message.
    if (turnstileEl && turnstileWidgetId === null) turnstileEl.setAttribute("data-turnstile-unavailable", "");
    reportTurnstileUnavailable(reason, code);
  }
  function showTurnstileFallback(el) {
    el.textContent = "We couldn\u2019t load our security check, which can happen with some network or security settings. Email us at ";
    var a = document.createElement("a");
    a.href = "mailto:" + FALLBACK_EMAIL;
    a.textContent = FALLBACK_EMAIL;
    el.appendChild(a);
    el.appendChild(document.createTextNode(" and we\u2019ll set up your community."));
  }

  function renderTurnstile() {
    if (!TURNSTILE_SITEKEY) return;
    if (!turnstileEl) { markTurnstileUnavailable("missing"); return; }
    if (!window.turnstile) {
      if (turnstileUnavailable) return;
      if (Date.now() - turnstileWaitStart < TURNSTILE_TIMEOUT_MS) { setTimeout(renderTurnstile, 150); return; }
      markTurnstileUnavailable("timeout");
      return;
    }
    if (turnstileWidgetId !== null) return;
    // api.js arrived (maybe late): back to the normal checkbox.
    turnstileUnavailable = false;
    clearError("turnstile");
    turnstileEl.removeAttribute("data-turnstile-unavailable");
    turnstileWidgetId = window.turnstile.render(turnstileEl, {
      sitekey: TURNSTILE_SITEKEY,
      theme: "auto",
      callback: function (t) { turnstileToken = t; turnstileUnavailable = false; turnstileErrors = 0; clearError("turnstile"); },
      "error-callback": function (code) {
        turnstileToken = null;
        var c = String(code || "");
        var retryable = /^[36]\d{5}$/.test(c) || /^1106/.test(c);
        if (!retryable || ++turnstileErrors >= TURNSTILE_MAX_RETRYABLE_ERRORS) markTurnstileUnavailable("error", code);
        return true;
      },
      "expired-callback": function () { turnstileToken = null; }
    });
  }
  function resetTurnstile() {
    turnstileToken = null;
    if (turnstileWidgetId !== null && window.turnstile) {
      try { window.turnstile.reset(turnstileWidgetId); } catch (e) { /* noop */ }
    }
  }
  var turnstileScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
  if (turnstileScript) turnstileScript.addEventListener("load", renderTurnstile);
  renderTurnstile();

  // ── Validation (UX only; worker is authoritative) ─────────────────────────
  function parseDevices(s) {
    if (typeof s !== "string" || s.trim() === "") return NaN;
    var n = Number(s);
    return Number.isInteger(n) ? n : NaN;
  }

  function val(el) { return el && el.value ? el.value.trim() : ""; }

  function validate() {
    var errs = [];
    if (!val(els.display_name)) errs.push(["display_name", "Please enter a community or page name."]);
    if (!EMAIL_RE.test(val(els.organizer_email))) errs.push(["organizer_email", "Please enter a valid email address."]);
    if (val(els.organizer_phone).length > 40) errs.push(["organizer_phone", "Please enter a shorter phone number."]);
    var ot = val(els.org_type);
    if (ot && ORG_TYPES.indexOf(ot) === -1) errs.push(["org_type", "Please choose a valid organization type."]);
    var mh = els.market_handle.value;
    if (!mh || MARKET_HANDLES.indexOf(mh) === -1) errs.push(["market_handle", "Please choose a valid community location."]);
    var city = val(els.city);
    if (!city) errs.push(["city", "Please enter your city."]);
    else if (city.length > 100) errs.push(["city", "Please enter a shorter city name."]);
    var regionWord = (REGION_LABELS[mh] || "State").toLowerCase();
    if (!els.region.value) errs.push(["region", "Please choose your " + regionWord + "."]);
    else if (!regionName(mh, els.region.value)) errs.push(["region", "Please choose a " + regionWord + " from the list."]);
    var ds = els.discovery_source.value;
    if (!ds) errs.push(["discovery_source", "Please tell us how you heard about us."]);
    else if (DISCOVERY_SOURCES.indexOf(ds) === -1) errs.push(["discovery_source", "Please choose an option from the list."]);
    else if (ds === "other") {
      var other = val(els.discovery_source_other);
      if (!other) errs.push(["discovery_source_other", "Please tell us where you heard about us."]);
      else if (other.length > 200) errs.push(["discovery_source_other", "Please keep this shorter."]);
    }
    if (nonprofitOptedIn()) {
      var np = val(els.nonprofit_name);
      if (!np) errs.push(["nonprofit_name", "Please enter the nonprofit's name."]);
      else if (np.length > 200) errs.push(["nonprofit_name", "Please enter a shorter nonprofit name."]);
    }
    var devices = parseDevices(els.target_devices.value);
    if (!Number.isInteger(devices)) errs.push(["target_devices", "Please enter the number of devices."]);
    else if (devices < MIN_DEVICES) errs.push(["target_devices", "The community program requires a minimum of " + MIN_DEVICES + " devices."]);
    var opens = els.window_opens_at.value;
    var closes = els.window_closes_at.value;
    if (!opens) errs.push(["window_opens_at", "Please choose a start date."]);
    if (!closes) errs.push(["window_closes_at", "Please choose an end date."]);
    if (opens && closes) {
      if (opens < today) errs.push(["window_opens_at", "Window open date can't be in the past."]);
      if (closes <= opens) errs.push(["window_closes_at", "Window close date must be after the open date."]);
      else if (closes > addDays(opens, MAX_WINDOW_DAYS)) errs.push(["window_closes_at", "The order window can't be longer than " + MAX_WINDOW_DAYS + " days."]);
    }
    return errs;
  }

  function clearError(field) {
    var el = form.querySelector('[data-error-for="' + field + '"]');
    if (el) el.textContent = "";
  }
  function clearAllErrors() {
    var nodes = form.querySelectorAll("[data-error-for]");
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = "";
    if (formError) { formError.hidden = true; formError.textContent = ""; }
  }
  function showErrors(pairs) {
    var first = null;
    pairs.forEach(function (p) {
      var el = form.querySelector('[data-error-for="' + p[0] + '"]');
      if (el && !el.textContent) {
        if (typeof p[1] === "function") p[1](el); else el.textContent = p[1];
        if (!first) first = el;
      }
    });
    if (first && first.scrollIntoView) first.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  function showFormError(msg) {
    if (!formError) return;
    formError.textContent = msg;
    formError.hidden = false;
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  var FATAL = "Something went wrong creating your community page. Please contact lauren@tincan.kids for help.";

  form.addEventListener("submit", function (event) {
    event.preventDefault();
    if (submitBtn.disabled) return;
    clearAllErrors();

    var errs = validate();
    // Once the check is unavailable, the email fallback, not "complete the
    // verification" with nothing to complete.
    if (TURNSTILE_SITEKEY && !turnstileToken) {
      errs.push(["turnstile", turnstileUnavailable ? showTurnstileFallback : "Please complete the verification above."]);
    }
    if (errs.length > 0) { showErrors(errs); return; }

    var email = val(els.organizer_email);
    var payload = {
      display_name: val(els.display_name),
      organizer_name: val(els.organizer_name) || undefined,
      organizer_email: email,
      organizer_phone: val(els.organizer_phone) || undefined,
      org_type: val(els.org_type) || undefined,
      market_handle: els.market_handle.value,
      city: val(els.city),
      region: els.region.value,
      // Klaviyo's "City, State" text, also what a worker from before the
      // city/region fields required (ENG-2171).
      community_city_state: val(els.city) + ", " + regionName(els.market_handle.value, els.region.value),
      discovery_source: els.discovery_source.value,
      discovery_source_other: els.discovery_source.value === "other" ? val(els.discovery_source_other) : undefined,
      target_devices: parseDevices(els.target_devices.value),
      window_opens_at: els.window_opens_at.value,
      window_closes_at: els.window_closes_at.value,
      community_blurb: val(els.community_blurb) || undefined,
      marketing_consent: true,
      turnstileToken: turnstileToken
    };
    if (nonprofitOptedIn()) {
      payload.nonprofit_opt_in = true;
      payload.nonprofit_name = val(els.nonprofit_name);
    }

    var fd = new FormData();
    fd.append("payload", JSON.stringify(payload));

    submitBtn.disabled = true;
    submitBtn.textContent = "Submitting…";

    fetch(WORKER_BASE + "/api/submit", { method: "POST", body: fd })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.status === "ok") { showSuccess(res.organizerEmail || email); return; }
        if (res && res.status === "invalid") {
          showErrors((res.errors || []).map(function (e) { return [e.field, e.message]; }));
          resetTurnstile();
          restoreButton();
          return;
        }
        showFormError((res && res.message) || FATAL);
        resetTurnstile();
        restoreButton();
      })
      .catch(function () {
        showFormError(FATAL);
        resetTurnstile();
        restoreButton();
      });
  });

  function restoreButton() {
    submitBtn.disabled = false;
    submitBtn.textContent = "Submit";
  }

  function showSuccess(email) {
    if (successEmail) successEmail.textContent = email || "";
    form.hidden = true;
    form.style.display = "none";
    if (successBox) {
      successBox.hidden = false;
      if (successBox.scrollIntoView) successBox.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
})();
